import React, { useState } from 'react';
import { Star, ChevronLeft, ChevronRight, Quote } from 'lucide-react';
import { testimonials, featuredQuote } from '../data/mock';

const TestimonialsSection = () => {
  const [activeIndex, setActiveIndex] = useState(0);
  const next = () => setActiveIndex((prev) => (prev + 1) % testimonials.length);
  const prev = () => setActiveIndex((prev) => (prev - 1 + testimonials.length) % testimonials.length);

  return (
    <section className="relative py-12 md:py-16 bg-black overflow-hidden">
      <div className="relative z-10 max-w-[1200px] mx-auto px-6 lg:px-12">
        {/* Section Header */}
        <div className="text-center mb-10">
          <div className="inline-flex items-center gap-2 px-4 py-2 bg-[#59d862]/10 border border-[#59d862]/30 rounded-full mb-3">
            <Star className="w-4 h-4 text-[#59d862]" fill="#59d862" />
            <span className="text-[#59d862] text-sm font-medium">Success Stories</span>
          </div>
          <h2 className="text-2xl md:text-4xl lg:text-5xl font-bold heading">
            What Agents <span className="text-[#59d862]">Say</span>
          </h2>
        </div>

        {/* Featured Quote - Sean Barrett */}
        <div className="mb-12">
          <div className="relative p-6 md:p-10 bg-gradient-to-r from-[#004048]/20 via-[#004048]/10 to-transparent border border-[#59d862]/20 rounded-2xl overflow-hidden">
            {/* Quote icon */}
            <Quote className="absolute top-4 left-4 w-12 h-12 text-[#59d862]/20" />
            
            <div className="flex flex-col md:flex-row gap-6 md:gap-10 items-center relative z-10">
              {/* Image */}
              <div className="flex-shrink-0">
                <div className="relative">
                  <div className="w-28 h-28 md:w-36 md:h-36 overflow-hidden rounded-2xl ring-4 ring-[#59d862]/30">
                    <img 
                      src={featuredQuote.image}
                      alt={featuredQuote.name}
                      className="w-full h-full object-cover object-top"
                    />
                  </div>
                  {/* Glow effect */}
                  <div className="absolute inset-0 rounded-2xl ring-4 ring-[#59d862]/50 animate-pulse" />
                </div>
              </div>

              {/* Quote Content */}
              <div className="flex-1 text-center md:text-left">
                <blockquote className="text-lg md:text-xl lg:text-2xl text-white/90 leading-relaxed mb-4 italic">
                  "{featuredQuote.quote}"
                </blockquote>
                <div className="flex flex-col md:flex-row md:items-center gap-2 md:gap-4">
                  <div className="text-white font-bold text-lg heading">{featuredQuote.name}</div>
                  <div className="hidden md:block w-1.5 h-1.5 bg-[#59d862] rounded-full" />
                  <div className="text-[#59d862] text-sm">{featuredQuote.role}</div>
                </div>
              </div>
            </div>

            {/* Decorative corner */}
            <div className="absolute bottom-0 right-0 w-32 h-32 bg-gradient-to-tl from-[#59d862]/10 to-transparent" />
          </div>
        </div>

        {/* Agent Testimonials Carousel */}
        <div className="max-w-3xl mx-auto">
          <div className="p-6 md:p-8 bg-white/[0.02] border border-white/[0.06] rounded-2xl text-center relative">
            {/* Stars */}
            <div className="flex justify-center gap-1 mb-4">
              {[...Array(testimonials[activeIndex].rating)].map((_, i) => (
                <Star key={i} className="w-4 h-4 text-[#59d862]" fill="#59d862" />
              ))}
            </div>

            {/* Quote */}
            <blockquote className="text-lg md:text-xl text-white/90 leading-relaxed mb-6">
              "{testimonials[activeIndex].quote}"
            </blockquote>

            {/* Author */}
            <div className="flex items-center justify-center gap-3">
              <div className="w-10 h-10 bg-[#004048] rounded-xl flex items-center justify-center">
                <span className="text-[#59d862] font-bold text-sm">
                  {testimonials[activeIndex].name.split(' ').map(n => n[0]).join('')}
                </span>
              </div>
              <div className="text-left">
                <div className="text-white font-semibold text-sm">{testimonials[activeIndex].name}</div>
                <div className="text-white/50 text-xs">{testimonials[activeIndex].role}</div>
              </div>
            </div>
          </div>

          {/* Navigation */}
          <div className="flex items-center justify-center gap-3 mt-4">
            <button 
              onClick={prev} 
              className="w-8 h-8 border border-white/20 rounded-lg flex items-center justify-center hover:border-[#59d862] hover:bg-[#59d862]/10 transition-colors"
              aria-label="Previous testimonial"
            >
              <ChevronLeft className="w-4 h-4 text-white" />
            </button>
            <div className="flex gap-1.5">
              {testimonials.map((_, i) => (
                <button 
                  key={i} 
                  onClick={() => setActiveIndex(i)} 
                  className={`h-2 rounded-full transition-all ${i === activeIndex ? 'bg-[#59d862] w-5' : 'bg-white/30 w-2 hover:bg-white/50'}`}
                  aria-label={`Go to testimonial ${i + 1}`}
                />
              ))}
            </div>
            <button 
              onClick={next} 
              className="w-8 h-8 border border-white/20 rounded-lg flex items-center justify-center hover:border-[#59d862] hover:bg-[#59d862]/10 transition-colors"
              aria-label="Next testimonial"
            >
              <ChevronRight className="w-4 h-4 text-white" />
            </button>
          </div>
        </div>
      </div>
    </section>
  );
};

export default TestimonialsSection;
